/**
 * LoginForm Component - Modern Design
 * Simplified form with clean inputs and modern styling
 * Follows React conventions and composition patterns
 */


import React from 'react';
import { Form, Input, Button } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { LoginFormProps, LoginCredentials } from '../../types/auth.types';
import { useResponsiveDesign } from '../../hooks/useResponsiveDesign';
import styles from './LoginForm.module.scss';

const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  loading = false,
  className = "",
}) => {
  const { isMobile } = useResponsiveDesign();
  const [form] = Form.useForm();

  const handleFinish = async (values: LoginCredentials): Promise<void> => {
    await onSubmit(values);
  };


  return (
    <>
      <Form
        form={form}
        name="loginForm"
        onFinish={handleFinish}
        layout="vertical"
        requiredMark={false}
        className={`${styles.loginForm} ${className}`}
        autoComplete="off"
        style={{ background: 'transparent', padding: 0 }}
      >
        <Form.Item
          label={<span style={{ fontWeight: 500, fontSize: 15 }}>User ID</span>}
          name="username"
          rules={[
            { required: true, message: 'Please enter your user ID!' },
            { min: 3, message: 'User ID must be at least 3 characters!' }
          ]}
          className={styles.formItem}
        >
          <Input
            prefix={<UserOutlined className={styles.inputIcon} aria-hidden="true" />}
            placeholder="Enter your user ID"
            size="large"
            className={styles.formInput}
            aria-label="User ID"
            autoComplete="username"
            style={{ borderRadius: 8, fontSize: 15 }}
          />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: 500, fontSize: 15 }}>Password</span>}
          name="password"
          rules={[
            { required: true, message: 'Please enter your password!' },
            { min: 6, message: 'Password must be at least 6 characters!' }
          ]}
          className={styles.formItem}
        >
          <Input.Password
            prefix={<LockOutlined className={styles.inputIcon} aria-hidden="true" />}
            placeholder="Enter your password"
            size="large"
            className={styles.formInput}
            aria-label="Password"
            autoComplete="current-password"
            style={{ borderRadius: 8, fontSize: 15 }}
          />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <a href="#" className={styles.forgotPasswordLink} style={{ fontSize: 14, color: '#1890ff', fontWeight: 500 }}>
            Forgot password?
          </a>
        </div>

        <Form.Item className={styles.submitButton} style={{ marginBottom: 0 }}>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            size="large"
            className={styles.signInButton}
            disabled={loading}
            aria-label="Sign in to your account"
            style={{ borderRadius: 8, fontWeight: 600, fontSize: 16, height: 48 }}
          >
            SIGN IN
          </Button>
        </Form.Item>
      </Form>
    </>
  );
};

LoginForm.displayName = 'LoginForm';

export default LoginForm;